import { Vue, Component, Watch } from "vue-property-decorator";
import { sortBy } from "lodash";
import { ReportsModule } from "@/modules/reports-objective";
import { fmtMsg, ActionItem } from "commonui";
import { Checkbox, Select } from "ant-design-vue";
import { ReportsLocale } from "@/locales/localeid";
import Spinning from "@/components/spinning/spinning.vue";
import LabelText from "@/components/label-text/label-text.vue";
import UnitSelect from "@/components/unit-select/unit-select.vue";
import { AccessLevel, MethodologyType, MethodologyTypeEnum } from "@/util/enum";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { ILessonPlanVersionSimpleModel } from "@/models/lesson-plan";
import { ILessonPlanVersionService, LessonPlanVersionService } from "@/services/lesson-plan";
import { VersionGroupModule } from "@/modules/versiongroup";
import { MethodologyTypeItems } from "@/util/constants";

@Component({
	components: {
		Select,
		Checkbox,
		LabelText,
		Spinning,
		ActionItem,
		Option: Select.Option,
		UnitSelect
	}
})
export default class TeachingPlanHeaderActions extends Vue {
	selectedContentId: any = "";
	accessLevel = AccessLevel;
	public reportsLocale = ReportsLocale;
	public lessonPlanVersions: ILessonPlanVersionSimpleModel[] = [];
	public selectedLPV: ILessonPlanVersionSimpleModel = {
		id: "",
		name: "",
		description: "",
		methodology: -1,
		status: -1,
		versionGroupId: "",
		combinedNameMethodology: ""
	};
	public publishedLabel: string = "(Published)";
	public unpublishedLabel: string = "(Unpublished)";
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();

	created() {
		ReportsModule.setMethodologyType(this.selectedLPV.methodology);
	}

	async mounted() {
		const selectedVersionGroups = [VersionGroupModule.selectedGsVersion, VersionGroupModule.selectedLsVersion];
		const methodologyTypes = [
			MethodologyTypeEnum.Classic,
			MethodologyTypeEnum.Digital,
			MethodologyTypeEnum.Independent,
			MethodologyTypeEnum.Remote,
			MethodologyTypeEnum.REP
		];
		this.lessonPlanVersions = await this.lessonPlanVersionService.getLessonPlanVersionsByTypes(
			selectedVersionGroups,
			methodologyTypes
		);
		if (this.lessonPlanVersions.length) {
			this.selectedLPV = this.lessonPlanVersions[0];
			ReportsModule.getTitleTextList(this.selectedLPV.methodology);
		}
	}

	get methodologyText(): string {
		return fmtMsg(ReportsLocale.ReportsComponentMethodology);
	}

	get componentText(): string {
		return fmtMsg(ReportsLocale.ReportsComponentText);
	}

	get vcsLevelUnits() {
		return sortBy(ReportsModule.vcsUnits, "unit");
	}

	get contents() {
		return sortBy(ReportsModule.contents, "title");
	}

	get exportText() {
		return fmtMsg(ReportsLocale.ReportsExportText);
	}

	get exportAllText() {
		return fmtMsg(ReportsLocale.ReportsExportAllText);
	}

	get filterMethodologyType(): any {
		return Object.values(MethodologyType).filter((d) => isNaN(d as any));
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	setSelectedUnit(value) {
		ReportsModule.setSelectedUnit(value);
		if (this.selectedLPV.methodology !== MethodologyTypeEnum.All) {
			ReportsModule.getTitleTextList(this.selectedLPV.methodology);
		}
		ReportsModule.setCurrentVersionGroup();
	}

	exportPDF() {
		ReportsModule.getComponentObjectivesPDF({
			lessonPlanVersionId: this.selectedLPV.id,
			methodology: this.selectedLPV.methodology
		});
	}

	exportPDFAll() {
		ReportsModule.getComponentObjectivesPDFZip({
			lessonPlanVersionId: this.selectedLPV.id,
			methodology: this.selectedLPV.methodology
		});
	}

	handleMethodologyChange(value) {
		// If current or previous value is/was REP, because REP methodology has different title list.
		ReportsModule.setMethodologyType(value);
		if (value === MethodologyType.REP || this.selectedLPV.methodology === MethodologyType.REP) {
			ReportsModule.getTitleTextList(this.selectedLPV.methodology);
		}
	}

	handleContentChange(value) {
		this.selectedContentId = value;
		ReportsModule.setContentId(value);
	}

	@Watch("contents", { deep: true })
	getcontents(value) {
		this.selectedContentId = this.contents.length ? this.contents[0].id : "";
		ReportsModule.setContentId(this.selectedContentId);
	}

	@Watch("selectedContentId", { immediate: true })
	@Watch("selectedLPV", { immediate: true })
	getContentExposures() {
		if (this.selectedContentId) {
			ReportsModule.getContentExposures(this.selectedLPV.id);
		} else {
			ReportsModule.setContentExposures([]);
		}
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	handleChangeLPV(lpvId: string) {
		const newLpvSelected = this.lessonPlanVersions.find((lpv) => lpv.id === lpvId);
		this.selectedLPV = newLpvSelected as ILessonPlanVersionSimpleModel;
		ReportsModule.getTitleTextList(this.selectedLPV.methodology);
	}

	getMethodologyName(methodology: number) {
		return MethodologyTypeItems.get(methodology);
	}
}
